import { Injectable } from '@angular/core';
import { ApiResponse } from '../api-service/api-types.model';
import { ApiType, getApiClient } from '../axios-service/axios.config';
import { ApiService } from '../api-service/safe-api-call';
import { AppMasterContext, CreateAppContextRequest, UpdateAppMasterContextRequest } from '../../types/master-context.model';

@Injectable({
    providedIn: 'root',
})
export class AppContextRepository {
    private sharedClient = getApiClient(ApiType.Shared);

    constructor(
        private apiService: ApiService
    ) { }

    /**
     * Create a new master context (Designation, Department, etc.)
     * @param path The specific endpoint to call (CreateOrgContext or CreateSchoolContext)
     * @param req The creation request
     */
    async create(path: string, req: CreateAppContextRequest): Promise<ApiResponse<AppMasterContext>> {
        return this.apiService.safeApiCall(
            async () => {
                const res = await this.sharedClient.post<ApiResponse<AppMasterContext>>(path, req);
                return res.data;
            },
            'Failed to create master context'
        );
    }

    /**
     * Fetch a specific master context by ID
     */
    async getById(id: string): Promise<ApiResponse<AppMasterContext>> {
        return this.apiService.safeApiCall(
            async () => {
                const res = await this.sharedClient.get<ApiResponse<AppMasterContext>>(`api/AppMasterContext/GetContextById?id=${id}`);
                return res.data;
            },
            'Failed to fetch master context'
        );
    }

    /**
     * Fetch master contexts by their category (e.g., 'DESGN', 'DEPT')
     */
    async getSchoolCategory(context: string, tenantCode: string, schoolCode: string): Promise<ApiResponse<AppMasterContext[]>> {
        return this.apiService.safeApiCall(
            async () => {
                const res = await this.sharedClient.get<ApiResponse<AppMasterContext[]>>(
                    `api/AppMasterContext/GetSchoolContextByContexts?context=${context}&tenantCode=${tenantCode}&schoolCode=${schoolCode}`
                );
                return res.data;
            },
            'Failed to fetch master contexts'
        );
    }

    async getOrgCategory(context: string, tenantCode: string): Promise<ApiResponse<AppMasterContext[]>> {
        return this.apiService.safeApiCall(
            async () => {
                const res = await this.sharedClient.get<ApiResponse<AppMasterContext[]>>(
                    `api/AppMasterContext/GetOrgContextByContexts?context=${context}&tenantCode=${tenantCode}`
                );
                return res.data;
            },
            'Failed to fetch master contexts'
        );
    }

    /**
     * Update an existing master context
     */
    async update(req: UpdateAppMasterContextRequest): Promise<ApiResponse<AppMasterContext>> {
        return this.apiService.safeApiCall(
            async () => {
                const res = await this.sharedClient.post<ApiResponse<AppMasterContext>>('api/AppMasterContext/UpdateContext', req);
                return res.data;
            },
            'Failed to update master context'
        );
    }

    /**
     * Delete a master context
     */
    async delete(id: string): Promise<ApiResponse<any>> {
        return this.apiService.safeApiCall(
            async () => {
                const res = await this.sharedClient.delete<ApiResponse<any>>(`api/AppMasterContext/DeleteContext?id=${id}`);
                return res.data;
            },
            'Failed to delete master context'
        );
    }

    /**
     * Toggle active status of a master context
     */
    async toggleStatus(id: string): Promise<ApiResponse<any>> {
        return this.apiService.safeApiCall(
            async () => {
                const res = await this.sharedClient.get<ApiResponse<any>>(`api/AppMasterContext/ToggleContext?id=${id}`);
                return res.data;
            },
            'Failed to update status'
        );
    }
}
